import { SIGNAL_OUT_OF_TESTING_SCOPE_ERROR } from '@terra/shared-asset-processing';
import { TRACE_BULK_DELETE_MAX_THREADS } from '@terra/shared-contracts-trace';
import { getAuthHeaders } from '../utils/authHeaders';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';

export type NeoRunResponse = {
  success: boolean;
  threadId: string | null;
  executionId: string | null;
  error?: string;
};

export async function resumeNeoAttack({
  threadId,
  applicationId,
}: {
  threadId: string;
  applicationId: string;
}) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${getCurrentGatewayUrl()}/ai/agents/neo/resume`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ threadId, applicationId }),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to resume Neo');
  }
}

export async function runNeoAttack({
  signalId,
  applicationId,
}: {
  signalId: string;
  applicationId: string;
}): Promise<NeoRunResponse> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${getCurrentGatewayUrl()}/ai/agents/neo/run`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ signalId, applicationId }),
  });

  if (!response.ok) {
    throw new Error('Failed to start Neo');
  }

  return (await response.json()) as NeoRunResponse;
}

export function countResults(results: PromiseSettledResult<unknown>[]) {
  const succeeded = results.filter(
    (result) => result.status === 'fulfilled',
  ).length;
  const failed = results.filter(
    (result) => result.status === 'rejected',
  ).length;

  return { succeeded, failed };
}

export function chunkThreadIds(
  threadIds: readonly string[],
  chunkSize = TRACE_BULK_DELETE_MAX_THREADS,
): string[][] {
  return Array.from(
    { length: Math.ceil(threadIds.length / chunkSize) },
    (_, index) => threadIds.slice(index * chunkSize, (index + 1) * chunkSize),
  );
}

export function pluralize(count: number, singular: string) {
  return count === 1 ? singular : `${singular}s`;
}

export function isNeoOutOfScopeError(error?: string) {
  return error === SIGNAL_OUT_OF_TESTING_SCOPE_ERROR;
}
